import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';
import {
  useUpdateMeetingStatusMutation, useCancelMeetingMutation, useRescheduleMeetingMutation,
  useGetMeetingNotesQuery, useAddMeetingNoteMutation,
  useGetRemindersQuery, useCreateReminderMutation,
} from '@/redux/internal-services/workspaceApiExtensions';
import { IcoCalendar, ContextTypeIcon, ENTITY_ROUTE } from '@/components/Tasks/TaskCard';

// ─── Config ────────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  scheduled:   { bg: 'bg-blue-50',  text: 'text-blue-700',  label: 'Scheduled',   bar: 'bg-blue-300' },
  rescheduled: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Rescheduled', bar: 'bg-amber-400' },
  completed:   { bg: 'bg-green-50', text: 'text-green-700', label: 'Done',        bar: 'bg-green-300' },
  cancelled:   { bg: 'bg-gray-100', text: 'text-gray-500',  label: 'Cancelled',   bar: 'bg-gray-300' },
  no_show:     { bg: 'bg-red-50',   text: 'text-red-600',   label: 'No Show',     bar: 'bg-red-400' },
} as const;


const THRESHOLD = 90;
const ACTIONABLE = new Set(['scheduled', 'rescheduled']);

const NOTE_CATEGORIES = [
  { value: 'general',              label: 'General' },
  { value: 'customer_requirement', label: 'Customer Req.' },
  { value: 'decision',             label: 'Decision' },
  { value: 'follow_up',            label: 'Follow Up' },
  { value: 'measurement',          label: 'Measurement' },
  { value: 'risk',                 label: 'Risk' },
  { value: 'scope_change',         label: 'Scope Change' },
  { value: 'site_condition',       label: 'Site Condition' },
];

// ─── Inline expanded detail ────────────────────────────────────────────────────

interface MeetingInlineDetailProps {
  meeting: MeetingFull;
  showReschedule: boolean;
  setShowReschedule: React.Dispatch<React.SetStateAction<boolean>>;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
}

function MeetingInlineDetail({ meeting, showReschedule, setShowReschedule, onStartNoteTaker }: MeetingInlineDetailProps) {
  const navigate = useNavigate();
  const [rescheduleMeeting, { isLoading: rescheduling }] = useRescheduleMeetingMutation();
  const [addNote, { isLoading: addingNote }] = useAddMeetingNoteMutation();
  const [createReminder, { isLoading: creatingReminder }] = useCreateReminderMutation();
  const { data: notes = [] } = useGetMeetingNotesQuery(meeting.meeting_id);
  const { data: remindersRes } = useGetRemindersQuery({ source_entity_id: meeting.meeting_id, status: 'active' });

  const [noteText, setNoteText] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [rescheduleStart, setRescheduleStart] = useState('');
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [reminderDt, setReminderDt] = useState('');

  const activeReminderCount = (remindersRes as { data?: unknown[] } | undefined)?.data?.length ?? 0;

  const ctxHref = meeting.context?.entity_id
    ? (ENTITY_ROUTE[meeting.context.context_type]?.(meeting.context.entity_id) ?? null)
    : null;

  const handleReschedule = async () => {
    if (!rescheduleStart) return;
    const newStart = new Date(rescheduleStart);
    const origStart = meeting.start_time ? new Date(meeting.start_time) : null;
    const origEnd   = meeting.end_time   ? new Date(meeting.end_time)   : null;
    const durationMs = origStart && origEnd ? origEnd.getTime() - origStart.getTime() : 30 * 60 * 1000;
    const newEnd = new Date(newStart.getTime() + durationMs);
    await rescheduleMeeting({ id: meeting.meeting_id, start_time: newStart.toISOString(), end_time: newEnd.toISOString() });
    setShowReschedule(false);
    setRescheduleStart('');
  };

  const [showNoteForm, setShowNoteForm] = useState(false);

  return (
    <div className="space-y-1.5" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

      {/* ── Note Taker ─── */}
      {onStartNoteTaker && (
        <button
          type="button"
          onClick={() => onStartNoteTaker('meeting', meeting.meeting_id)}
          className="flex items-center gap-2 w-full rounded-xl border border-brand-bright/40 bg-brand-bright/5 hover:bg-brand-bright/10 px-3 py-2 text-xs font-medium text-brand-bright transition"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5 flex-shrink-0" aria-hidden>
            <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
            <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Start Note Taker
        </button>
      )}

      {/* ── Context link ─── */}
      {meeting.context?.display_label && (
        <div className="flex justify-end">
          {ctxHref ? (
            <button onClick={() => navigate(ctxHref)} className="flex items-center gap-1 text-[10px] text-brand-dark/70 hover:text-brand-dark truncate transition-colors max-w-full">
              <ContextTypeIcon type={meeting.context.context_type} />
              <span className="truncate">{meeting.context.display_label}</span>
            </button>
          ) : (
            <span className="flex items-center gap-1 text-[10px] text-ui-text3 truncate">
              <ContextTypeIcon type={meeting.context.context_type} />
              {meeting.context.display_label}
            </span>
          )}
        </div>
      )}

      {/* ── Reschedule picker ─── */}
      {showReschedule && (
        <div className="flex items-center gap-1.5">
          <input
            type="datetime-local"
            value={rescheduleStart}
            onChange={e => setRescheduleStart(e.target.value)}
            style={{ fontSize: 10 }}
            className="flex-1 h-6 border border-[#EDEDED] rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-brand-dark/30"
          />
          <button
            onClick={handleReschedule}
            disabled={!rescheduleStart || rescheduling}
            className="flex-shrink-0 text-[10px] px-2.5 h-6 rounded-lg bg-brand-dark text-white font-semibold hover:bg-brand-dark/85 disabled:opacity-50 transition-colors"
          >
            {rescheduling ? '…' : 'Set'}
          </button>
        </div>
      )}

      {/* ── Reminders — full-width clickable row ─── */}
      <button
        onClick={() => setShowAddReminder(v => !v)}
        className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-xl transition-colors group/bell ${
          showAddReminder ? 'bg-brand-dark/[0.04]' : 'hover:bg-brand-dark/[0.03]'
        }`}
      >
        <div className="relative flex-shrink-0">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="text-ui-text transition-colors"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {activeReminderCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] rounded-full bg-ui-text text-ui-card text-[8px] flex items-center justify-center font-bold px-0.5 leading-none">
              {activeReminderCount}
            </span>
          )}
        </div>
        <span className="text-[11px] font-medium text-ui-text transition-colors">
          {activeReminderCount > 0 ? `${activeReminderCount} reminder${activeReminderCount > 1 ? 's' : ''}` : 'Add reminder'}
        </span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
          className={`ml-auto flex-shrink-0 text-ui-text/40 transition-transform ${showAddReminder ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* ── Reminder picker ─── */}
      {showAddReminder && (
        <div className="flex items-center gap-1.5">
          <input
            type="datetime-local"
            value={reminderDt}
            onChange={e => setReminderDt(e.target.value)}
            style={{ fontSize: 10 }}
            className="flex-1 h-6 border border-[#EDEDED] rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-brand-dark/30"
          />
          <button
            onClick={async () => {
              if (!reminderDt) return;
              await createReminder({
                title: meeting.title,
                dtstart: new Date(reminderDt).toISOString(),
                source_entity_type: 'meeting',
                source_entity_id: meeting.meeting_id,
              });
              setShowAddReminder(false);
              setReminderDt('');
            }}
            disabled={!reminderDt || creatingReminder}
            className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-lg bg-brand-dark text-white font-semibold hover:bg-brand-dark/85 disabled:opacity-50 transition-colors"
          >
            {creatingReminder ? '…' : 'Set'}
          </button>
        </div>
      )}

      {/* ── Notes — header row + toggled form ─── */}
      <div className="space-y-1">
        {/* Header: pencil icon + count + "+" toggle */}
        <div className="flex items-center gap-1.5 px-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text flex-shrink-0">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          <span className="text-[11px] font-medium text-ui-text flex-1">
            {notes.length > 0 ? `${notes.length} note${notes.length > 1 ? 's' : ''}` : 'Notes'}
          </span>
          <button
            title={showNoteForm ? 'Close' : 'Add note'}
            onClick={() => setShowNoteForm(v => !v)}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
              showNoteForm ? 'bg-brand-dark/10 text-brand-dark' : 'text-ui-text3/40 hover:text-brand-dark hover:bg-brand-dark/[0.06]'
            }`}
          >
            {showNoteForm ? (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </button>
        </div>

        {/* Add note form */}
        {showNoteForm && (
          <div className="flex flex-col gap-1 bg-ui-subtle rounded-xl px-2 py-2">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              autoFocus
              className="w-full text-[10px] border border-[#EDEDED] rounded-lg px-2 py-1.5 bg-ui-card text-ui-text resize-none focus:outline-none focus:ring-1 focus:ring-brand-dark/30"
            />
            <div className="flex gap-1.5">
              <select
                value={noteCategory}
                onChange={e => setNoteCategory(e.target.value)}
                className="flex-1 text-[10px] h-6 border border-[#EDEDED] rounded-lg px-1.5 bg-ui-card text-ui-text focus:outline-none"
              >
                {NOTE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button
                onClick={async () => {
                  if (!noteText.trim()) return;
                  await addNote({ meetingId: meeting.meeting_id, content: noteText.trim(), category: noteCategory });
                  setNoteText('');
                  setNoteCategory('general');
                  setShowNoteForm(false);
                }}
                disabled={!noteText.trim() || addingNote}
                className="flex-shrink-0 text-[10px] px-2.5 h-6 rounded-lg bg-brand-dark text-white font-semibold hover:bg-brand-dark/85 disabled:opacity-50 transition-colors"
              >
                {addingNote ? '…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Notes list */}
        {notes.length > 0 && (
          <div className="space-y-1">
            {notes.map(n => (
              <div key={n.note_id} className="text-[10px] bg-ui-subtle rounded-lg px-2 py-1.5">
                <p className="text-ui-text leading-snug">{n.content}</p>
                <p className="text-ui-text3/50 mt-0.5">
                  {NOTE_CATEGORIES.find(c => c.value === n.category)?.label ?? n.category}
                  {n.author_name ? ` · ${n.author_name}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const IcoChevron = ({ open }: { open: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  meeting: MeetingFull;
  onSelect?: (meetingId: string) => void;
  expandedId?: string | null;
  onExpand?: (id: string | null) => void;
  compact?: boolean;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
}

const WorkspaceMeetingCard: React.FC<Props> = ({
  meeting,
  onSelect,
  expandedId,
  onExpand,
  compact = false,
  onStartNoteTaker,
}) => {
  const [updateStatus] = useUpdateMeetingStatusMutation();
  const [cancelMeeting] = useCancelMeetingMutation();
  const [showReschedule, setShowReschedule] = useState(false);

  // ── Expand state (controlled or local) ──────────────────────────────────────
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = onExpand !== undefined ? expandedId === meeting.meeting_id : localExpanded;
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && onExpand && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const toggleExpand = () => {
    if (onExpand) {
      onExpand(isExpanded ? null : meeting.meeting_id);
    } else {
      setLocalExpanded(v => !v);
    }
  };

  // ── Swipe drag state ─────────────────────────────────────────────────────────
  const [dragX, setDragXState] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragXRef = useRef(0);
  const startX = useRef(0);
  const startY = useRef(0);
  const moved = useRef(false);
  const active = useRef(false);

  const setDragX = useCallback((x: number) => { dragXRef.current = x; setDragXState(x); }, []);

  const isActionable = ACTIONABLE.has(meeting.status);

  const resolveDrag = useCallback(() => {
    active.current = false;
    setDragging(false);
    const dx = dragXRef.current;
    if (dx >= THRESHOLD) {
      setDragX(180);
      setTimeout(() => { updateStatus({ id: meeting.meeting_id, status: 'completed' }); setDragX(0); }, 290);
    } else if (dx <= -THRESHOLD) {
      setDragX(-180);
      setTimeout(() => { updateStatus({ id: meeting.meeting_id, status: 'cancelled' }); setDragX(0); }, 290);
    } else {
      setDragX(0);
    }
  }, [meeting.meeting_id, updateStatus, setDragX]);


  // ── Mouse drag ───────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    if (active.current) return;
    active.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    moved.current = false;
    setDragging(true);

    const onMove = (me: MouseEvent) => {
      const dx = me.clientX - startX.current;
      const dy = me.clientY - startY.current;
      if (!moved.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      moved.current = true;
      if (!isActionable) return;
      setDragX(Math.max(-130, Math.min(130, dx)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resolveDrag();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isActionable, resolveDrag, setDragX]);

  // ── Touch drag ───────────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (active.current) return;
    active.current = true;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    moved.current = false;
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!active.current) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (!moved.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    moved.current = true;
    if (Math.abs(dy) > Math.abs(dx)) { resolveDrag(); return; }
    if (!isActionable) return;
    e.preventDefault();
    setDragX(Math.max(-130, Math.min(130, dx)));
  }, [isActionable, resolveDrag, setDragX]);

  const onTouchEnd = useCallback(() => { resolveDrag(); }, [resolveDrag]);

  // ── Click vs swipe ───────────────────────────────────────────────────────────
  const handleClick = () => {
    if (moved.current) { moved.current = false; return; }
    if (onSelect) {
      onSelect(meeting.meeting_id);
    } else {
      toggleExpand();
    }
  };

  // ── Date formatting ──────────────────────────────────────────────────────────
  const now = new Date();
  const start = meeting.start_time ? new Date(meeting.start_time) : null;
  const end = meeting.end_time ? new Date(meeting.end_time) : null;
  const isPast = start ? start < now && !['completed', 'cancelled'].includes(meeting.status) : false;
  const startStr = start
    ? start.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        ...(start.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
      })
    : null;
  const endStr = end ? end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null;

  const sc = STATUS_CFG[meeting.status as keyof typeof STATUS_CFG]
    ?? { bg: 'bg-gray-50', text: 'text-gray-500', label: meeting.status, bar: 'bg-gray-300' };

  const rightP = dragX > 0 ? Math.min(1, dragX / THRESHOLD) : 0;
  const leftP = dragX < 0 ? Math.min(1, -dragX / THRESHOLD) : 0;

  return (
    <div ref={cardRef} className="relative rounded-2xl overflow-hidden select-none touch-pan-y">

      {/* Swipe right → Complete background */}
      <div
        className="absolute inset-0 rounded-2xl flex items-center justify-start pl-4"
        style={{ background: '#22c55e', opacity: rightP > 0 ? 1 : 0, pointerEvents: 'none' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>

      {/* Swipe left → Cancel background */}
      <div
        className="absolute inset-0 rounded-2xl flex items-center justify-end pr-4"
        style={{ background: '#ef4444', opacity: leftP > 0 ? 1 : 0, pointerEvents: 'none' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </div>

      {/* Card */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleClick}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(.22,1,.36,1)',
          cursor: 'pointer',
          willChange: 'transform',
        }}
        className={`relative rounded-2xl border bg-ui-card overflow-hidden ${
          isExpanded
            ? 'border-brand-dark/25 shadow-md'
            : 'border-[#EDEDED]'
        }`}
      >
        {/* Left status bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${sc.bar}`} />

        <div className={compact ? 'pl-3 pr-2 pt-1.5 pb-1.5 ml-2' : 'pl-4 pr-4 pt-3 pb-3 ml-2'}>

          {/* Title row: icon + title + action circles (or badge if not actionable) */}
          <div className={`flex items-center gap-1.5 ${compact ? 'mb-1' : 'mb-1.5'}`}>
            <IcoCalendar className={`flex-shrink-0 ${sc.text} ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
            <h3 className={`flex-1 font-semibold text-ui-text leading-snug ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-1'}`}>
              {meeting.title}
            </h3>
            <div className="flex-shrink-0 flex items-center gap-1 ml-1">
              {isActionable && !compact ? (
                <>
                  <button title="Complete" onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); updateStatus({ id: meeting.meeting_id, status: 'completed' }); }}
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-green-50 text-green-500 hover:bg-green-100 hover:text-green-600 transition-colors">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button title="No Show" onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); updateStatus({ id: meeting.meeting_id, status: 'no_show' }); }}
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-50 text-amber-500 hover:bg-amber-100 hover:text-amber-600 transition-colors">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <line x1="17" y1="11" x2="23" y2="17"/><line x1="23" y1="11" x2="17" y2="17"/>
                    </svg>
                  </button>
                  <button title="Reschedule" onMouseDown={e => e.stopPropagation()}
                    onClick={e => { e.stopPropagation(); setShowReschedule(v => !v); if (!isExpanded) toggleExpand(); }}
                    className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${showReschedule ? 'bg-brand-dark/10 text-brand-dark' : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                      <path d="M17 14.5a4 4 0 1 0-1 3.46"/><polyline points="17 19 17 14.5 13 14.5"/>
                    </svg>
                  </button>
                  <button title="Cancel meeting" onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); cancelMeeting({ id: meeting.meeting_id }); }}
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 transition-colors">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </>
              ) : (
                <span className={`font-bold rounded-full uppercase tracking-wide ${sc.bg} ${sc.text} ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2.5 py-1'}`}>
                  {sc.label}
                </span>
              )}
            </div>
          </div>

          {/* Meta row: date | status badge (actionable) | chevron */}
          <div className={`flex items-center gap-2 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
            <span className={`flex items-center gap-1 font-medium flex-1 min-w-0 ${isPast ? 'text-red-500' : 'text-ui-text3'}`}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {startStr ?? '—'}{endStr ? ` – ${endStr}` : ''}
            </span>
            {isActionable && !compact && (
              <span className={`font-bold rounded-full uppercase tracking-wide flex-shrink-0 ${sc.bg} ${sc.text} text-[9px] px-2 py-0.5`}>
                {sc.label}
              </span>
            )}
            {!compact && <IcoChevron open={isExpanded} />}
          </div>

          {/* Inline expanded detail */}
          {isExpanded && (
            <div className="mt-1.5 pt-1.5 border-t border-[#EDEDED]/50">
              <MeetingInlineDetail
                meeting={meeting}
                showReschedule={showReschedule}
                setShowReschedule={setShowReschedule}
                onStartNoteTaker={onStartNoteTaker}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceMeetingCard;
